import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { instructorAuthService, InstructorAuthProfile } from '../services/instructorAuthService';

interface InstructorAuthContextType {
  // État d'authentification
  user: User | null;
  instructor: InstructorAuthProfile | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions d'authentification
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  
  // Vérification des permissions
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  
  // Gestion du profil
  updateProfile: (updates: Partial<InstructorAuthProfile>) => Promise<boolean>;
  refreshProfile: () => Promise<void>;
}

const InstructorAuthContext = createContext<InstructorAuthContextType | undefined>(undefined);

interface InstructorAuthProviderProps {
  children: React.ReactNode;
}

export const InstructorAuthProvider: React.FC<InstructorAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [instructor, setInstructor] = useState<InstructorAuthProfile | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialisation de l'authentification au montage du composant
  useEffect(() => {
    checkInitialAuth();
  }, []);

  /**
   * Vérifier l'authentification initiale
   */
  const checkInitialAuth = async () => {
    try {
      setIsLoading(true);
      
      const response = await instructorAuthService.checkSession();
      
      if (response.success && response.user && response.instructor) {
        setUser(response.user);
        setInstructor(response.instructor);
        setPermissions(response.instructor.permissions);
        setIsAuthenticated(true);
      } else {
        // Session invalide, nettoyer l'état
        clearAuthState();
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification initiale:', error);
      clearAuthState();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Connexion
   */
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const response = await instructorAuthService.login(email, password);
      
      if (response.success && response.user && response.instructor) {
        setUser(response.user);
        setInstructor(response.instructor);
        setPermissions(response.instructor.permissions);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        clearAuthState();
        return { 
          success: false, 
          error: response.error || 'Erreur de connexion' 
        };
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      clearAuthState();
      return { 
        success: false, 
        error: 'Une erreur inattendue s\'est produite' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Déconnexion
   */
  const logout = async (): Promise<void> => {
    try {
      await instructorAuthService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      clearAuthState();
    }
  };

  /**
   * Nettoyer l'état d'authentification
   */
  const clearAuthState = () => {
    setUser(null);
    setInstructor(null);
    setPermissions([]);
    setIsAuthenticated(false);
  };

  /**
   * Vérifier une permission spécifique
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Vérifier si l'utilisateur a au moins une des permissions
   */
  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => permissions.includes(permission));
  };

  /**
   * Vérifier si l'utilisateur a toutes les permissions
   */
  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => permissions.includes(permission));
  };

  /**
   * Mettre à jour le profil instructeur
   */
  const updateProfile = async (updates: Partial<InstructorAuthProfile>): Promise<boolean> => {
    if (!instructor) return false;

    try {
      const success = await instructorAuthService.updateProfile(instructor.id, updates);
      
      if (success) {
        // Actualiser le profil local
        setInstructor(prev => prev ? { ...prev, ...updates } : null);
      }
      
      return success;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      return false;
    }
  };

  /**
   * Actualiser le profil depuis la base de données
   */
  const refreshProfile = async (): Promise<void> => {
    if (!user) return;

    try {
      const profile = await instructorAuthService.getInstructorProfileWithPermissions(user.id);
      
      if (profile) {
        setInstructor(profile);
        setPermissions(profile.permissions);
      }
    } catch (error) {
      console.error('Erreur lors de l\'actualisation du profil:', error);
    }
  };

  const contextValue: InstructorAuthContextType = {
    // État
    user,
    instructor,
    permissions,
    isAuthenticated,
    isLoading,
    
    // Actions
    login,
    logout,
    
    // Permissions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // Profil
    updateProfile,
    refreshProfile
  };

  return (
    <InstructorAuthContext.Provider value={contextValue}>
      {children}
    </InstructorAuthContext.Provider>
  );
};

/**
 * Hook pour utiliser le contexte d'authentification instructeur
 */
export const useInstructorAuthContext = (): InstructorAuthContextType => {
  const context = useContext(InstructorAuthContext);
  
  if (context === undefined) {
    throw new Error('useInstructorAuthContext must be used within an InstructorAuthProvider');
  }
  
  return context;
};

/**
 * Hook pour vérifier les permissions avec redirection automatique
 */
export const useInstructorPermissionsGuard = (
  requiredPermissions: string | string[] = [],
  redirectTo: string = '/instructor/unauthorized',
  requireAll: boolean = false
) => {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    instructor, 
    isLoading, 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions 
  } = useInstructorAuthContext();

  useEffect(() => {
    if (isLoading) return;

    // Vérifier l'authentification
    if (!isAuthenticated || !instructor) {
      navigate('/instructor/login');
      return;
    }

    // Si aucune permission spécifique n'est requise
    if (!requiredPermissions || 
        (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)) {
      return;
    }

    // Normaliser en array
    const permsArray = Array.isArray(requiredPermissions) 
      ? requiredPermissions 
      : [requiredPermissions];

    // Vérifier les permissions
    let hasAccess = false;
    
    if (requireAll) {
      hasAccess = hasAllPermissions(permsArray);
    } else {
      hasAccess = hasAnyPermission(permsArray);
    }

    if (!hasAccess) {
      navigate(redirectTo);
    }
  }, [
    isLoading, 
    isAuthenticated, 
    instructor, 
    requiredPermissions, 
    requireAll, 
    redirectTo, 
    navigate,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  ]);

  return {
    isLoading,
    isAuthenticated,
    instructor,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
};

// Import pour useNavigate
import { useNavigate } from 'react-router-dom';

export default InstructorAuthContext;
